import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApiReady } from '@/hooks/useApiReady'
import resourceCommentService, {
  type ResourceCommentType,
} from '@/services/resourceCommentService'

export const RESOURCE_COMMENTS_INITIAL_VISIBLE_COUNT = 5

export const resourceCommentsKeys = {
  all: ['resourceComments'] as const,
  list: (
    resourceType: ResourceCommentType,
    resourceId: string,
    teamId: string | undefined,
    visibleCount: number
  ) => [...resourceCommentsKeys.all, 'list', resourceType, resourceId, teamId, visibleCount] as const,
}

export const commentUnreadSummaryKeys = {
  all: ['commentUnreadSummary'] as const,
  summary: (teamId: string | undefined, userId: string | undefined) =>
    [...commentUnreadSummaryKeys.all, teamId, userId] as const,
}

function invalidateNotificationQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: commentUnreadSummaryKeys.all })
}

export function buildResourceCommentsQueryOptions(
  params: {
    resourceType: ResourceCommentType
    resourceId: string
    teamId: string
    visibleCount: number
  },
  queryClient: ReturnType<typeof useQueryClient>
) {
  const { resourceType, resourceId, teamId, visibleCount } = params

  return {
    queryKey: resourceCommentsKeys.list(resourceType, resourceId, teamId, visibleCount),
    queryFn: async () => {
      const result = await resourceCommentService.getComments({
        resourceType,
        resourceId,
        limit: visibleCount,
      })
      queryClient.invalidateQueries({ queryKey: commentUnreadSummaryKeys.all })
      return result
    },
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  }
}

export function useResourceComments(params: {
  resourceType: ResourceCommentType
  resourceId: string | undefined
  teamId: string | undefined
  visibleCount: number
}) {
  const apiReady = useApiReady()
  const queryClient = useQueryClient()
  const { resourceType, resourceId, teamId, visibleCount } = params

  return useQuery({
    ...buildResourceCommentsQueryOptions(
      {
        resourceType,
        resourceId: resourceId ?? '',
        teamId: teamId ?? '',
        visibleCount,
      },
      queryClient
    ),
    enabled: apiReady && !!resourceId && !!teamId,
  })
}

/** Démarre le fetch des commentaires en parallèle du détail mission / CRA. */
export function usePrefetchResourceComments(params: {
  resourceType: ResourceCommentType
  resourceId: string | undefined
  teamId: string | undefined
}) {
  return useResourceComments({
    ...params,
    visibleCount: RESOURCE_COMMENTS_INITIAL_VISIBLE_COUNT,
  })
}

export function useCommentUnreadSummary(
  teamId: string | undefined,
  userId: string | undefined,
  options?: { enabled?: boolean }
) {
  const apiReady = useApiReady()
  const fetchEnabled = options?.enabled !== false

  return useQuery({
    queryKey: commentUnreadSummaryKeys.summary(teamId, userId),
    queryFn: async () => {
      if (!teamId) return []
      return resourceCommentService.getUnreadSummary(teamId)
    },
    enabled: fetchEnabled && apiReady && !!teamId && !!userId,
    staleTime: 60 * 1000,
    refetchInterval: 3 * 60 * 1000,
  })
}

function invalidateCommentQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  resourceType?: ResourceCommentType,
  resourceId?: string
) {
  queryClient.invalidateQueries({ queryKey: resourceCommentsKeys.all })
  invalidateNotificationQueries(queryClient)
  if (resourceType && resourceId) {
    queryClient.invalidateQueries({
      queryKey: [...resourceCommentsKeys.all, 'list', resourceType, resourceId],
    })
  }
}

export function useCreateResourceComment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: resourceCommentService.createComment,
    onSuccess: (_data, variables) => {
      invalidateCommentQueries(queryClient, variables.resourceType, variables.resourceId)
    },
  })
}

export function useUpdateResourceComment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ commentId, body }: { commentId: string; body: string }) =>
      resourceCommentService.updateComment(commentId, body),
    onSuccess: () => {
      invalidateCommentQueries(queryClient)
    },
  })
}

export function useDeleteResourceComment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (commentId: string) => resourceCommentService.deleteComment(commentId),
    onSuccess: () => {
      invalidateCommentQueries(queryClient)
    },
  })
}
